/**
 * Authentication Hook Module
 * A custom React hook for managing user authentication operations
 * Handles login and logout functionality with localStorage management
 */

import dynamicAPI from "./dynamic-API";
import { useMutation, useQueryClient } from "react-query";
import useAuth from "../../../hooks/use-auth";

/**
 * Base API endpoint for authentication operations
 */
const url = "/api/auth";

/**
 * Custom hook for managing authentication state
 * Provides login mutation and logout functionality
 *
 * @returns {Object} Object containing authentication functions
 * @returns {Object} postLogin - Mutation object for user login
 * @returns {Function} postLogout - Function to handle user logout
 */
const useAuthHook = () => {
  const { setAuth } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Mutation hook for user login
   * Handles authentication request and cache invalidation
   */
  const postLogin = useMutation(
    async (obj) => await dynamicAPI("post", `${url}/login`, obj),
    {
      retry: 0,
      onSuccess: () => queryClient.invalidateQueries(["login"]),
    },
  );

  /**
   * Handles user logout
   * Clears authentication state and removes user data from localStorage
   *
   * @returns {void} Returns the result of removing userInfo from localStorage
   */
  const postLogout = () => {
    setAuth(null);
    localStorage.removeItem("userRole");
    return localStorage.removeItem("userInfo");
  };

  return { postLogin, postLogout };
};

export default useAuthHook;
